import React, { useState, useEffect, useRef, useMemo, useCallback, useTransition, useDeferredValue } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { apiClient, validateSession } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Filter, RefreshCw, Download, X, ChevronDown, ChevronUp, Calendar, Eye, EyeOff, Archive, Clock } from "lucide-react";
import { 
  SmartFiltersPanel, 
  DEFAULT_SMART_FILTERS, 
  calculateMaxValues, 
  applySmartFilters,
  getSmartFilterRowClass,
  getFilterHeaderRow
} from "../components/SmartFilters";
import { toast } from "sonner";

// BroadcastChannel for session sync
const SESSION_BROADCAST_CHANNEL = 'alpha_session_sync';

// Broadcast channel for receiving live tick data
// BroadcastChannel removed - pop-out now only receives from backend storage per user request
// const BROADCAST_CHANNEL_NAME = 'ticklog_sync';

// Column definitions - same as main TickHistory + LTP Diff (Pop-out exclusive)
const COLUMNS = [
  { key: 'ltp', label: 'LTP' },
  { key: 'ltp_diff', label: 'LTP Diff' },
  { key: 'bid_price', label: 'Bid Price' },
  { key: 'ask_price', label: 'Ask Price' },
  { key: 'bid_qty', label: 'Bid Qty' },
  { key: 'ask_qty', label: 'Ask Qty' },
  { key: 'trade_pos', label: 'Agg %' },
  { key: 'spread', label: 'Spread' },
  { key: 'ltq', label: 'LTQ' },
  { key: 'vol_chg', label: 'Vol Chg' },
  { key: 'volume', label: 'Volume' },
  { key: 'oi', label: 'OI' },
  { key: 'oi_change', label: 'OI Chg' },
  { key: 'time', label: 'Time (IST)' },
];

// Calculate Trade Position % - where the trade occurred within the spread
const calculateTradePosition = (tick) => {
  const ltp = tick.last_price || tick.ltp || 0;
  const bid = tick.bid_price || 0;
  const ask = tick.ask_price || 0;
  const spread = ask - bid;
  
  if (spread <= 0 || bid <= 0 || ask <= 0) return null;
  
  return ((ltp - bid) / spread) * 100;
};

// Default visible columns - includes ltp_diff for pop-out
const DEFAULT_VISIBLE_COLS = ['ltp', 'ltp_diff', 'bid_price', 'ask_price', 'bid_qty', 'ask_qty', 'trade_pos', 'spread', 'ltq', 'vol_chg', 'time'];

// Column widths
const COLUMN_MIN_WIDTHS = {
  ltp: 95, ltp_diff: 75, bid_price: 90, ask_price: 90, bid_qty: 75, ask_qty: 75,
  trade_pos: 70, spread: 55, ltq: 65, vol_chg: 70, volume: 75,
  oi: 75, oi_change: 65, time: 85,
};

// Formatters
const formatNumber = (num, decimals = 2) => {
  if (num === undefined || num === null) return "-";
  return Number(num).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatVolume = (num) => {
  if (!num) return "-";
  if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
  if (num >= 100000) return (num / 100000).toFixed(2) + " L";
  if (num >= 1000) return (num / 1000).toFixed(1) + " K";
  return num.toLocaleString('en-IN');
};

const formatTimeIST = (ts) => {
  if (!ts) return "-";
  const d = new Date(ts);
  // Force IST timezone for consistent display regardless of user's location
  return d.toLocaleTimeString('en-IN', { 
    hour12: false, 
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const getSeconds = (ts) => ts ? new Date(ts).getSeconds() : -1;

// Pre-calculate ltp_diff for an array of ticks (sorted newest first)
// This runs ONCE when ticks are set, not on every render
const preCalculateLtpDiff = (ticks) => {
  if (!ticks || ticks.length === 0) return ticks;
  
  for (let i = 0; i < ticks.length; i++) {
    if (i === ticks.length - 1) {
      ticks[i].ltp_diff = 0;
    } else {
      const currentLtp = ticks[i].last_price || 0;
      const prevLtp = ticks[i + 1].last_price || 0;
      ticks[i].ltp_diff = currentLtp - prevLtp;
    }
  }
  return ticks;
};

// GRADIENT Data bar component - MEMOIZED for performance
const DataBar = React.memo(({ value, max, color, showSign = false, originalValue = null }) => {
  // Use originalValue for sign display if provided, otherwise use value
  const displayValue = originalValue !== null ? originalValue : value;
  const isNegative = displayValue < 0;
  const absValue = Math.abs(value);
  
  const pct = max > 0 ? Math.min((absValue / max) * 100, 100) : 0;
  const intensity = 0.3 + (pct / 100) * 0.7;
  
  const gradients = {
    green: `linear-gradient(90deg, rgba(34,197,94,0.15) 0%, rgba(34,197,94,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    red: `linear-gradient(90deg, rgba(239,68,68,0.15) 0%, rgba(239,68,68,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    blue: `linear-gradient(90deg, rgba(59,130,246,0.15) 0%, rgba(59,130,246,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    yellow: `linear-gradient(90deg, rgba(234,179,8,0.15) 0%, rgba(234,179,8,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    cyan: `linear-gradient(90deg, rgba(6,182,212,0.15) 0%, rgba(6,182,212,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    purple: `linear-gradient(90deg, rgba(168,85,247,0.15) 0%, rgba(168,85,247,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    orange: `linear-gradient(90deg, rgba(249,115,22,0.15) 0%, rgba(249,115,22,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
  };
  
  // Format the display text with sign if needed
  const displayText = showSign && isNegative ? `-${formatVolume(absValue)}` : formatVolume(absValue);
  
  return (
    <div className="relative h-full flex items-center">
      <div className="absolute inset-0 rounded" style={{ background: gradients[color] || gradients.blue }} />
      <span className="relative z-10 px-1 text-[11px] font-mono text-white font-medium">{displayText}</span>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value && 
         prevProps.max === nextProps.max && 
         prevProps.color === nextProps.color &&
         prevProps.showSign === nextProps.showSign &&
         prevProps.originalValue === nextProps.originalValue;
});

// ==================== FILTER SAVE MODE UTILITIES ====================
const MAX_SAVED_TICKS = 50000;
const FILTER_SAVE_MODES = { OFF: 'off', SAVE: 'save', SHOW_ALL: 'show_all', HIDE: 'hide' };

// Market close times for auto-download (IST)
const MARKET_CLOSE_TIMES = {
  NSE: { hour: 15, minute: 31 },
  MCX: { hour: 23, minute: 55 },
};

// Generate unique tick ID for deduplication
const getTickUniqueId = (tick) => {
  if (tick.tick_id) return tick.tick_id;
  const ts = tick.timestamp_ms || tick.exchange_timestamp || Date.now();
  const ltq = tick.last_traded_quantity || tick.ltq || 0;
  const ltp = tick.last_price || 0;
  const token = tick.instrument_token || '';
  return `${token}_${ts}_${ltq}_${ltp}`;
};

// CSV Download utility - now accepts visible columns and order
const downloadTicksAsCSV = (ticks, symbol, visibleColumns = null, columnOrder = null) => {
  if (!ticks || ticks.length === 0) {
    alert('No ticks to download');
    return;
  }
  
  // Determine which columns to export
  let columnsToExport = COLUMNS;
  if (visibleColumns && columnOrder) {
    columnsToExport = columnOrder
      .filter(key => visibleColumns.includes(key))
      .map(key => COLUMNS.find(c => c.key === key))
      .filter(Boolean);
  } else if (visibleColumns) {
    columnsToExport = COLUMNS.filter(c => visibleColumns.includes(c.key));
  }
  
  const headers = ['Symbol', 'Timestamp', ...columnsToExport.map(c => c.label)];
  
  const rows = ticks.map(tick => {
    const row = [
      symbol || tick.trading_symbol || 'Unknown',
      tick.timestamp || new Date(tick.timestamp_ms || tick.exchange_timestamp).toISOString(),
    ];
    
    columnsToExport.forEach(col => {
      switch(col.key) {
        case 'ltp': row.push(tick.last_price || 0); break;
        case 'ltp_diff': row.push(tick.ltp_diff || 0); break;
        case 'bid_price': row.push(tick.bid_price || 0); break;
        case 'ask_price': row.push(tick.ask_price || 0); break;
        case 'bid_qty': row.push(tick.bid_qty || 0); break;
        case 'ask_qty': row.push(tick.ask_qty || 0); break;
        case 'trade_pos': {
          const pos = calculateTradePosition(tick);
          row.push(pos !== null ? pos.toFixed(2) : '');
          break;
        }
        case 'spread': row.push(tick.spread || ((tick.ask_price || 0) - (tick.bid_price || 0))); break;
        case 'ltq': row.push(tick.last_traded_quantity || 0); break;
        case 'vol_chg': row.push(tick.volume_change || 0); break;
        case 'volume': row.push(tick.volume || tick.volume_traded || 0); break;
        case 'oi': row.push(tick.oi || 0); break;
        case 'oi_change': row.push(tick.oi_change || 0); break;
        case 'time': row.push(tick.timestamp || ''); break;
        default: row.push('');
      }
    });
    
    return row;
  });
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => {
      const str = String(cell);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    }).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const date = new Date().toISOString().split('T')[0];
  const time = new Date().toTimeString().split(' ')[0].replace(/:/g, '-');
  link.href = url;
  link.download = `${symbol || 'ticks'}_filtered_${date}_${time}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Filter Save Mode Toggle UI Component
const FilterSaveModeToggle = ({ mode, setMode, savedCount, onDownload, onClear, autoDownload, setAutoDownload, compact = false }) => {
  const buttonSize = compact ? 'h-5 px-1.5 text-[8px]' : 'h-6 px-2 text-[9px]';
  const iconSize = compact ? 'w-2.5 h-2.5' : 'w-3 h-3';
  
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => setMode(mode === FILTER_SAVE_MODES.SAVE ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.SAVE)}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          mode === FILTER_SAVE_MODES.SAVE 
            ? 'bg-green-500/30 text-green-300 border border-green-400 shadow-lg shadow-green-500/20' 
            : 'bg-[#1a1a1a] text-green-400 border border-green-500/40 hover:bg-green-500/10'
        }`}
        title="Save filtered results"
      >
        <Archive className={iconSize} />
        Save
        {savedCount > 0 && <span className="ml-0.5 text-[7px] opacity-80">({savedCount})</span>}
      </button>
      
      <button
        onClick={() => setMode(mode === FILTER_SAVE_MODES.SHOW_ALL ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.SHOW_ALL)}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          mode === FILTER_SAVE_MODES.SHOW_ALL 
            ? 'bg-blue-500/30 text-blue-300 border border-blue-400 shadow-lg shadow-blue-500/20' 
            : 'bg-[#1a1a1a] text-blue-400 border border-blue-500/40 hover:bg-blue-500/10'
        }`}
        title="Show all saved + current filtered results"
      >
        <Eye className={iconSize} />
        Show All
      </button>
      
      <button
        onClick={() => setMode(mode === FILTER_SAVE_MODES.HIDE ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.HIDE)}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          mode === FILTER_SAVE_MODES.HIDE 
            ? 'bg-orange-500/30 text-orange-300 border border-orange-400 shadow-lg shadow-orange-500/20' 
            : 'bg-[#1a1a1a] text-orange-400 border border-orange-500/40 hover:bg-orange-500/10'
        }`}
        title="Hide all, only show NEW matches"
      >
        <EyeOff className={iconSize} />
        Hide
      </button>
      
      {/* Separator */}
      <div className="w-px h-4 bg-[#333] mx-0.5" />
      
      {/* Download CSV Button - Always visible */}
      <button
        onClick={onDownload}
        disabled={savedCount === 0}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          savedCount > 0 
            ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400 hover:bg-cyan-500/30' 
            : 'bg-[#1a1a1a] text-cyan-400 border border-cyan-500/40 hover:bg-cyan-500/10'
        }`}
        title={savedCount > 0 ? `Download ${savedCount} saved ticks as CSV` : 'No saved ticks (click Save first)'}
      >
        <Download className={iconSize} />
        CSV {savedCount > 0 && `(${savedCount})`}
      </button>
      
      {/* Auto-Download Toggle */}
      <button
        onClick={() => setAutoDownload(!autoDownload)}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          autoDownload 
            ? 'bg-purple-500/30 text-purple-300 border border-purple-400 shadow-lg shadow-purple-500/20' 
            : 'bg-[#1a1a1a] text-purple-400 border border-purple-500/40 hover:bg-purple-500/10'
        }`}
        title="Auto-download CSV at market close (3:31 PM NSE / 11:55 PM MCX) then clear data"
      >
        <Clock className={iconSize} />
        Auto
      </button>
      
      {savedCount > 0 && (
        <button
          onClick={onClear}
          className={`${buttonSize} rounded flex items-center gap-0.5 font-medium bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all`}
          title="Clear all saved ticks"
        >
          <X className={iconSize} />
        </button>
      )}
    </div>
  );
};

export default function PopOutTickLog() {
  const [searchParams] = useSearchParams();
  const token = parseInt(searchParams.get('token') || '0');
  const symbol = searchParams.get('symbol') || `Token ${token}`;
  const exchange = searchParams.get('exchange') || 'NSE';
  
  // State
  const [ticks, setTicks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [minLtq, setMinLtq] = useState(0);
  const [minBidQty, setMinBidQty] = useState(0);
  const [minAskQty, setMinAskQty] = useState(0);
  
  // Use deferred values for filters - prevents UI blocking during typing
  const deferredMinLtq = useDeferredValue(minLtq);
  const deferredMinBidQty = useDeferredValue(minBidQty);
  const deferredMinAskQty = useDeferredValue(minAskQty);
  
  // Bid/Ask Qty filter toggles and mode
  const [bidQtyEnabled, setBidQtyEnabled] = useState(false);
  const [askQtyEnabled, setAskQtyEnabled] = useState(false);
  const [bidAskQtyMode, setBidAskQtyMode] = useState('AND'); // 'AND' or 'OR'
  
  // Position % filters (Trade Position %) with AND/OR mode
  const [posLessThanEnabled, setPosLessThanEnabled] = useState(false);
  const [posLessThanValue, setPosLessThanValue] = useState('');
  const [posGreaterThanEnabled, setPosGreaterThanEnabled] = useState(false);
  const [posGreaterThanValue, setPosGreaterThanValue] = useState('');
  const [posFilterMode, setPosFilterMode] = useState('OR'); // 'AND' or 'OR'
  
  // LTP Diff filters with AND/OR mode (similar to AGG %)
  const [ltpDiffLessThanEnabled, setLtpDiffLessThanEnabled] = useState(false);
  const [ltpDiffLessThanValue, setLtpDiffLessThanValue] = useState('');
  const [ltpDiffGreaterThanEnabled, setLtpDiffGreaterThanEnabled] = useState(false);
  const [ltpDiffGreaterThanValue, setLtpDiffGreaterThanValue] = useState('');
  const [ltpDiffFilterMode, setLtpDiffFilterMode] = useState('OR'); // 'AND' or 'OR'
  
  // Volume Change and Spread filters
  const [minVolChg, setMinVolChg] = useState(0);
  const [spreadLessThanEnabled, setSpreadLessThanEnabled] = useState(false);
  const [spreadLessThanValue, setSpreadLessThanValue] = useState(0);
  const [spreadGreaterThanEnabled, setSpreadGreaterThanEnabled] = useState(false);
  const [spreadGreaterThanValue, setSpreadGreaterThanValue] = useState(0);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFullDay, setIsLoadingFullDay] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [totalTicks, setTotalTicks] = useState(0);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE_COLS);
  const [showColumns, setShowColumns] = useState(false);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true); // Auto-refresh ON by default for real-time updates
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0.5); // Default 30 seconds for faster updates
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingTicksCount, setPendingTicksCount] = useState(0);
  const lastTickTimestampRef = useRef(null);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState(null); // null = no sort (time order)
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  
  // Smart Filters state
  const [smartFilters, setSmartFilters] = useState(DEFAULT_SMART_FILTERS);
  
  // FILTER SAVE MODE - Save/Show All/Hide functionality with localStorage persistence
  const storageKeyPopout = `popout_${token}`;
  
  const [filterSaveMode, setFilterSaveMode] = useState(() => {
    try {
      const saved = localStorage.getItem(`filterSaveMode_${storageKeyPopout}`);
      return saved || FILTER_SAVE_MODES.OFF;
    } catch { return FILTER_SAVE_MODES.OFF; }
  });
  
  const [autoDownload, setAutoDownload] = useState(() => {
    try {
      return localStorage.getItem(`autoDownload_${storageKeyPopout}`) === 'true';
    } catch { return false; }
  });
  
  // Saved filtered ticks - Load from localStorage on mount
  const [savedTicks, setSavedTicks] = useState(() => {
    try {
      const saved = localStorage.getItem(`savedTicks_${storageKeyPopout}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const savedTickIdsRef = useRef(new Set(savedTicks.map(t => getTickUniqueId(t))));
  const hideTimestampRef = useRef(null);
  
  // Save filter mode to localStorage
  useEffect(() => {
    localStorage.setItem(`filterSaveMode_${storageKeyPopout}`, filterSaveMode);
    if (filterSaveMode === FILTER_SAVE_MODES.HIDE) {
      hideTimestampRef.current = Date.now();
    }
  }, [filterSaveMode, storageKeyPopout]);
  
  // Save auto-download setting
  useEffect(() => {
    localStorage.setItem(`autoDownload_${storageKeyPopout}`, autoDownload.toString());
  }, [autoDownload, storageKeyPopout]);
  
  // Save ticks to localStorage (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        const ticksToSave = savedTicks.slice(0, 10000);
        localStorage.setItem(`savedTicks_${storageKeyPopout}`, JSON.stringify(ticksToSave));
      } catch (e) {
        console.warn('Could not save ticks to localStorage:', e);
      }
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [savedTicks, storageKeyPopout]);
  
  // Manual Reference Values for bar % calculation - user inputs
  const [refValues, setRefValues] = useState(() => {
    try {
      const saved = localStorage.getItem(`popout_ref_values_${token}`);
      if (saved) return JSON.parse(saved);
    } catch {}
    return {};
  });
  
  // Column order state (for reordering) - ensures new columns are included
  const [columnOrder, setColumnOrder] = useState(() => {
    const defaultOrder = COLUMNS.map(c => c.key);
    try {
      const saved = localStorage.getItem('popout_column_order');
      if (saved) {
        const savedOrder = JSON.parse(saved);
        // Add any new columns that aren't in saved order
        const newCols = defaultOrder.filter(k => !savedOrder.includes(k));
        if (newCols.length > 0) {
          // Insert new columns after 'ltp' position or at start
          const ltpIndex = savedOrder.indexOf('ltp');
          if (ltpIndex >= 0) {
            savedOrder.splice(ltpIndex + 1, 0, ...newCols);
          } else {
            savedOrder.unshift(...newCols);
          }
        }
        return savedOrder;
      }
    } catch {}
    return defaultOrder;
  });
  
  const parentRef = useRef(null);
  
  // Refs for scroll-to-top tracking (must be declared before functions that use them)
  const lastTickCountRef = useRef(0);
  const hasScrolledToTopRef = useRef(false);
  
  // One-time migration: Ensure ltp_diff is in visibleCols for existing users
  useEffect(() => {
    if (!visibleCols.includes('ltp_diff')) {
      // Add ltp_diff after 'ltp' position
      const ltpIndex = visibleCols.indexOf('ltp');
      const newCols = [...visibleCols];
      if (ltpIndex >= 0) {
        newCols.splice(ltpIndex + 1, 0, 'ltp_diff');
      } else {
        newCols.unshift('ltp_diff');
      }
      setVisibleCols(newCols);
    }
  }, []); // Run only once on mount
  
  // Save column order to localStorage
  useEffect(() => {
    localStorage.setItem('popout_column_order', JSON.stringify(columnOrder));
  }, [columnOrder]);
  
  // Auto-download at market close
  useEffect(() => {
    if (!autoDownload || savedTicks.length === 0) return;
    
    const checkMarketClose = () => {
      const now = new Date();
      const istOffset = 5.5 * 60;
      const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
      const istMinutes = utcMinutes + istOffset;
      const istHour = Math.floor((istMinutes % (24 * 60)) / 60);
      const istMinute = Math.floor(istMinutes % 60);
      
      const today = new Date().toISOString().split('T')[0];
      const alreadyDownloaded = localStorage.getItem(`lastAutoDownload_${storageKeyPopout}`) === today;
      
      if (alreadyDownloaded) return;
      
      // Check NSE close time (3:31 PM)
      if ((exchange === 'NSE' || exchange === 'NFO' || exchange === 'BSE') && 
          istHour === 15 && istMinute === 31) {
        console.log(`[PopOut] Auto-downloading CSV at NSE market close`);
        downloadTicksAsCSV(savedTicks, symbol, visibleCols, columnOrder);
        localStorage.setItem(`lastAutoDownload_${storageKeyPopout}`, today);
        setSavedTicks([]);
        savedTickIdsRef.current.clear();
        return;
      }
      
      // Check MCX close time (11:55 PM)
      if (exchange === 'MCX' && istHour === 23 && istMinute === 55) {
        console.log(`[PopOut] Auto-downloading CSV at MCX market close`);
        downloadTicksAsCSV(savedTicks, symbol, visibleCols, columnOrder);
        localStorage.setItem(`lastAutoDownload_${storageKeyPopout}`, today);
        setSavedTicks([]);
        savedTickIdsRef.current.clear();
      }
    };
    
    const interval = setInterval(checkMarketClose, 60000);
    checkMarketClose();
    
    return () => clearInterval(interval);
  }, [autoDownload, savedTicks, symbol, storageKeyPopout, visibleCols, columnOrder, exchange]);
  
  // Download saved ticks as CSV (with visible columns)
  const downloadSavedTicks = useCallback(() => {
    downloadTicksAsCSV(savedTicks, symbol, visibleCols, columnOrder);
  }, [savedTicks, symbol, visibleCols, columnOrder]);
  
  // Clear saved ticks
  const clearSavedTicks = useCallback(() => {
    setSavedTicks([]);
    savedTickIdsRef.current.clear();
    hideTimestampRef.current = null;
    try {
      localStorage.removeItem(`savedTicks_${storageKeyPopout}`);
    } catch {}
  }, [storageKeyPopout]);
  
  // Set document title
  useEffect(() => {
    document.title = `${symbol} - Tick Log`;
  }, [symbol]);
  
  // SESSION VALIDATION - Listen for logout broadcasts from main app
  // This ensures pop-out windows are logged out when user logs in from another device
  useEffect(() => {
    // Check session validity on mount
    const checkSession = async () => {
      const isValid = await validateSession();
      if (!isValid) {
        toast.error('Session expired. This window will close.', { duration: 3000 });
        setTimeout(() => window.close(), 3000);
      }
    };
    checkSession();
    
    // Listen for logout broadcasts from other windows
    let channel = null;
    try {
      channel = new BroadcastChannel(SESSION_BROADCAST_CHANNEL);
      channel.onmessage = (event) => {
        if (event.data?.type === 'logout') {
          console.log('[PopOut] Received logout broadcast:', event.data);
          toast.error('You have been logged out from another device. This window will close.', { 
            duration: 3000,
            icon: '🔒'
          });
          // Clear local storage
          localStorage.removeItem('auth_token');
          localStorage.removeItem('session_id');
          localStorage.removeItem('user');
          // Close the pop-out window after showing message
          setTimeout(() => window.close(), 3000);
        }
      };
    } catch (err) {
      console.warn('[PopOut] BroadcastChannel not supported, using localStorage fallback');
    }
    
    // Also listen for localStorage changes (fallback for browsers without BroadcastChannel)
    const handleStorageChange = (e) => {
      if (e.key === 'session_logout_event') {
        try {
          const data = JSON.parse(e.newValue);
          if (data && Date.now() - data.timestamp < 5000) {
            toast.error('You have been logged out. This window will close.', { duration: 3000 });
            localStorage.removeItem('auth_token');
            localStorage.removeItem('session_id');
            localStorage.removeItem('user');
            setTimeout(() => window.close(), 3000);
          }
        } catch {}
      }
      // Also handle direct auth_token removal
      if (e.key === 'auth_token' && !e.newValue) {
        toast.error('Session ended. This window will close.', { duration: 3000 });
        setTimeout(() => window.close(), 3000);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    // Periodic session validation (every 30 seconds)
    const sessionCheckInterval = setInterval(async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast.error('Session expired. This window will close.', { duration: 3000 });
        setTimeout(() => window.close(), 3000);
        return;
      }
      
      try {
        const response = await apiClient.get('/user/validate-session');
        if (!response.data.valid) {
          toast.error('Session expired. This window will close.', { duration: 3000 });
          localStorage.removeItem('auth_token');
          localStorage.removeItem('session_id');
          localStorage.removeItem('user');
          setTimeout(() => window.close(), 3000);
        }
      } catch (err) {
        // Network error - don't close, just log
        console.warn('[PopOut] Session check failed:', err);
      }
    }, 30000);
    
    return () => {
      if (channel) {
        channel.close();
      }
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(sessionCheckInterval);
    };
  }, []);
  
  // Load available dates on mount
  useEffect(() => {
    const loadDates = async () => {
      console.log(`[PopOut] Loading dates for token: ${token}`);
      try {
        const response = await apiClient.get(`/history/ticks/dates/${token}`);
        console.log(`[PopOut] Dates response:`, response.data);
        if (response.data.dates && response.data.dates.length > 0) {
          setAvailableDates(response.data.dates);
          // Always select the first available date (most recent with data)
          const firstDate = response.data.dates[0].date;
          console.log(`[PopOut] Setting selectedDate to: ${firstDate}`);
          setSelectedDate(firstDate);
        } else {
          // No dates available - default to today
          const ist = new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
          const today = new Date(ist).toISOString().split('T')[0];
          console.log(`[PopOut] No dates found, defaulting to today: ${today}`);
          setSelectedDate(today);
        }
      } catch (err) {
        console.error('[PopOut] Error loading dates:', err);
        // If error, default to today
        const ist = new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
        const today = new Date(ist).toISOString().split('T')[0];
        setSelectedDate(today);
      }
    };
    if (token) {
      loadDates();
    }
  }, [token]);
  
  // Save manual reference values to localStorage when changed
  useEffect(() => {
    if (token) {
      localStorage.setItem(`popout_ref_values_${token}`, JSON.stringify(refValues));
    }
  }, [refValues, token]);
  
  // Note: Auto-refresh from backend is handled by the autoRefresh toggle effect at line 884
  // BroadcastChannel was removed per user request - pop-out only receives from backend storage
  
  // Load initial ticks when date changes
  useEffect(() => {
    if (!selectedDate) {
      console.log('[PopOut] No selectedDate yet, skipping load');
      return;
    }
    
    const loadInitial = async () => {
      console.log(`[PopOut] loadInitial triggered for date: ${selectedDate}`);
      setTicks([]);
      setSkip(0);
      setHasMore(true);
      setIsLoading(true);
      
      // Reset scroll tracking when date changes to force scroll to top
      hasScrolledToTopRef.current = false;
      lastTickCountRef.current = 0;
      
      try {
        const params = new URLSearchParams({ 
          limit: '5000',  // Load 5000 initially for better view
          skip: '0',
          latest_first: 'true'
        });
        params.append('date', selectedDate);
        
        console.log(`[PopOut] Loading ticks for token ${token}, date ${selectedDate}`);
        const response = await apiClient.get(`/history/ticks/${token}?${params}`);
        const data = response.data;
        console.log(`[PopOut] Received ${data.ticks?.length || 0} ticks, total: ${data.total}`);
        
        if (data.ticks && data.ticks.length > 0) {
          // Use ALL ticks (don't drop LTP-only ticks with 0 bid/ask).
          // Many real ticks have bid_price=0 or ask_price=0 (e.g. LTP-only updates or
          // partial WS frames). Filtering them out was making PopOut show 5-10% of
          // ticks vs Grid view's 100%. Keep everything except clearly empty rows.
          const validTicks = data.ticks.filter(t =>
            (t.last_price && t.last_price > 0) || (t.bid_price && t.bid_price > 0) || (t.ask_price && t.ask_price > 0)
          );
          
          if (validTicks.length > 0) {
            setTicks(validTicks);
            setSkip(validTicks.length);
            setTotalTicks(data.total || validTicks.length);
            setHasMore(data.has_more);
            
            // CRITICAL: Set lastTickTimestampRef to newest tick's timestamp
            // This allows auto-refresh to fetch only newer ticks
            if (validTicks[0]?.timestamp_ms) {
              lastTickTimestampRef.current = validTicks[0].timestamp_ms;
              console.log(`[PopOut] Set lastTickTimestamp to ${validTicks[0].timestamp_ms} (${validTicks[0].timestamp})`);
            }
            
            console.log(`[PopOut] State updated: ${validTicks.length} valid ticks loaded, total in DB: ${data.total}`);
          } else {
            console.log('[PopOut] No valid ticks after filtering');
            setHasMore(false);
            setTotalTicks(0);
          }
        } else {
          console.log('[PopOut] No ticks in response');
          setHasMore(false);
          setTotalTicks(0);
        }
      } catch (err) {
        console.error('[PopOut] Error loading ticks:', err);
      }
      setIsLoading(false);
    };
    
    loadInitial();
  }, [selectedDate, token]);
  
  // Load more ticks (for Load More button)
  const loadMoreTicks = async (reset = false) => {
    if (isLoading || (!reset && !hasMore)) return;
    setIsLoading(true);
    
    try {
      const currentSkip = reset ? 0 : skip;
      const params = new URLSearchParams({ 
        limit: '5000',  // Load 5000 at a time
        skip: currentSkip.toString(),
        latest_first: 'true'  // Get newest first
      });
      if (selectedDate) params.append('date', selectedDate);
      
      const response = await apiClient.get(`/history/ticks/${token}?${params}`);
      const data = response.data;
      
      if (data.ticks && data.ticks.length > 0) {
        // Keep ticks where ANY price field is non-zero (LTP-only ticks were being dropped)
        const validTicks = data.ticks.filter(t =>
          (t.last_price && t.last_price > 0) || (t.bid_price && t.bid_price > 0) || (t.ask_price && t.ask_price > 0)
        );
        
        if (validTicks.length > 0) {
          if (reset) {
            setTicks(validTicks);
            setSkip(validTicks.length);
          } else {
            // Append older ticks at the end
            setTicks(prev => [...prev, ...validTicks]);
            setSkip(s => s + validTicks.length);
          }
          setTotalTicks(prev => reset ? validTicks.length : prev + validTicks.length);
          setHasMore(data.has_more);
        } else {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading ticks:', err);
    }
    setIsLoading(false);
  };
  
  // Load FULL DAY ticks with incremental UI updates
  const loadFullDay = async () => {
    if (isLoadingFullDay) return;
    setIsLoadingFullDay(true);
    
    // Reset scroll tracking to force scroll to top after load
    hasScrolledToTopRef.current = false;
    lastTickCountRef.current = 0;
    
    try {
      let currentSkip = 0;
      let hasMoreData = true;
      let accumulated = [];
      
      while (hasMoreData) {
        const params = new URLSearchParams({ 
          limit: '5000', 
          skip: currentSkip.toString(),
          latest_first: 'true'  // Get newest first
        });
        if (selectedDate) params.append('date', selectedDate);
        
        const response = await apiClient.get(`/history/ticks/${token}?${params}`);
        const data = response.data;
        
        if (data.ticks && data.ticks.length > 0) {
          // Append to accumulated (already sorted newest first from API)
          accumulated = [...accumulated, ...data.ticks];
          currentSkip += data.ticks.length;
          hasMoreData = data.has_more;
          
          // Update UI incrementally for better feedback
          setTicks([...accumulated]);
          setSkip(currentSkip);
          setTotalTicks(data.total);
          
          // Small delay to allow UI to update and prevent request throttling
          await new Promise(r => setTimeout(r, 100));
        } else {
          hasMoreData = false;
        }
      }
      
      setHasMore(false);
    } catch (err) {
      console.error('Error loading full day:', err);
    }
    setIsLoadingFullDay(false);
  };
  
  // Load pending/new ticks since last load (for refresh)
  const loadPendingTicks = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    
    try {
      // Get the timestamp of the most recent tick we have
      const newestTick = ticks[0];
      const lastTimestamp = newestTick?.timestamp_ms || lastTickTimestampRef.current;
      
      if (!lastTimestamp) {
        // No ticks loaded yet, do a normal load
        await loadMoreTicks(true);
        setLastRefreshTime(new Date());
        setIsRefreshing(false);
        return;
      }
      
      // Fetch ticks newer than our latest
      const params = new URLSearchParams({ 
        limit: '10000',  // Get up to 10k new ticks
        after_timestamp: lastTimestamp.toString(),
        latest_first: 'true'
      });
      if (selectedDate) params.append('date', selectedDate);
      
      const response = await apiClient.get(`/history/ticks/${token}?${params}`);
      const data = response.data;
      
      if (data.ticks && data.ticks.length > 0) {
        console.log(`[PopOut] Auto-refresh received ${data.ticks.length} ticks from API`);
        
        // Prepend new ticks (they're newest first)
        setTicks(prev => {
          // Use tick_id for proper deduplication
          const existingIds = new Set(prev.map(t => t.tick_id || `${t.timestamp_ms}_${t.last_traded_quantity}_${t.last_price}`));
          const newTicks = data.ticks.filter(t => {
            const id = t.tick_id || `${t.timestamp_ms}_${t.last_traded_quantity}_${t.last_price}`;
            return !existingIds.has(id);
          });
          
          console.log(`[PopOut] After dedup: ${newTicks.length} new unique ticks to add`);
          setPendingTicksCount(newTicks.length);
          
          if (newTicks.length === 0) return prev;
          
          // Prepend new ticks at the beginning (newest first)
          const merged = [...newTicks, ...prev];
          console.log(`[PopOut] Total ticks after merge: ${merged.length}`);
          return merged;
        });
        
        // Update last timestamp to the newest tick received
        const newNewestTimestamp = data.ticks[0]?.timestamp_ms;
        if (newNewestTimestamp) {
          lastTickTimestampRef.current = newNewestTimestamp;
          console.log(`[PopOut] Updated lastTickTimestamp to ${newNewestTimestamp}`);
        }
      } else {
        console.log('[PopOut] No new ticks from auto-refresh');
        setPendingTicksCount(0);
      }
      setLastRefreshTime(new Date());
    } catch (err) {
      console.error('Error loading pending ticks:', err);
    }
    setIsRefreshing(false);
  };
  
  // Auto-refresh effect - refreshes from MongoDB at specified interval
  useEffect(() => {
    if (!autoRefresh || autoRefreshInterval <= 0) return;
    
    // Convert to milliseconds - support sub-minute intervals (in minutes)
    // 0.083 = 5 seconds, 0.167 = 10 seconds, 0.5 = 30 seconds
    const intervalMs = Math.round(autoRefreshInterval * 60 * 1000);
    
    console.log(`[PopOut] Auto-refresh enabled: every ${intervalMs/1000}s (${autoRefreshInterval} min)`);
    
    // Do an immediate refresh when auto-refresh is enabled
    const doRefresh = () => {
      console.log('[PopOut] Auto-refresh triggered - fetching latest ticks from MongoDB');
      loadPendingTicks();
    };
    
    doRefresh(); // Immediate first refresh when enabled
    
    const timer = setInterval(doRefresh, intervalMs);
    
    return () => {
      console.log('[PopOut] Auto-refresh cleared');
      clearInterval(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, autoRefreshInterval, selectedDate, token]);
  
  // Track last tick timestamp when ticks change
  useEffect(() => {
    if (ticks.length > 0) {
      const newestTick = ticks[0];
      lastTickTimestampRef.current = newestTick?.timestamp_ms || newestTick?.timestamp;
    }
  }, [ticks]);
  
  // Calculate AUTO max values from ALL ticks for proper bar scaling
  const autoMaxVals = useMemo(() => {
    // Sample from different parts of the array for better representation
    const sampleSize = Math.min(5000, ticks.length);
    const step = Math.max(1, Math.floor(ticks.length / sampleSize));
    const sample = [];
    for (let i = 0; i < ticks.length; i += step) {
      sample.push(ticks[i]);
    }
    
    return {
      maxBidQty: Math.max(1, ...sample.map(t => t.bid_qty || 0)),
      maxAskQty: Math.max(1, ...sample.map(t => t.ask_qty || 0)),
      maxLtq: Math.max(1, ...sample.map(t => t.last_traded_quantity || 0)),
      maxSpread: Math.max(1, ...sample.map(t => t.spread || 0)),
      maxVolChg: Math.max(1, ...sample.map(t => Math.abs(t.volume_change || 0))),
      maxVolume: Math.max(1, ...sample.map(t => t.volume || t.volume_traded || 0)),
      maxOi: Math.max(1, ...sample.map(t => t.oi || 0)),
      maxOiChg: Math.max(1, ...sample.map(t => Math.abs(t.oi_change || 0))),
    };
  }, [ticks]);
  
  // Final max values - use manual refValues if provided, else auto-calculated
  const maxVals = useMemo(() => {
    return {
      maxBidQty: refValues.bid_qty > 0 ? refValues.bid_qty : autoMaxVals.maxBidQty,
      maxAskQty: refValues.ask_qty > 0 ? refValues.ask_qty : autoMaxVals.maxAskQty,
      maxLtq: refValues.ltq > 0 ? refValues.ltq : autoMaxVals.maxLtq,
      maxSpread: refValues.spread > 0 ? refValues.spread : autoMaxVals.maxSpread,
      maxVolChg: refValues.vol_chg > 0 ? refValues.vol_chg : autoMaxVals.maxVolChg,
      maxVolume: refValues.volume > 0 ? refValues.volume : autoMaxVals.maxVolume,
      maxOi: refValues.oi > 0 ? refValues.oi : autoMaxVals.maxOi,
      maxOiChg: refValues.oi_change > 0 ? refValues.oi_change : autoMaxVals.maxOiChg,
    };
  }, [autoMaxVals, refValues]);
  
  // Get sortable value for a tick based on column key
  const getSortValue = useCallback((tick, key, prevTick) => {
    switch (key) {
      case 'ltp': return tick.last_price || 0;
      case 'ltp_diff': {
        if (!prevTick) return 0;
        return (tick.last_price || 0) - (prevTick.last_price || 0);
      }
      case 'bid_price': return tick.bid_price || 0;
      case 'ask_price': return tick.ask_price || 0;
      case 'bid_qty': return tick.bid_qty || 0;
      case 'ask_qty': return tick.ask_qty || 0;
      case 'trade_pos': return calculateTradePosition(tick) || 0;
      case 'spread': return tick.spread || 0;
      case 'ltq': return tick.last_traded_quantity || 0;
      case 'vol_chg': return tick.volume_change || 0;
      case 'volume': return tick.volume || tick.volume_traded || 0;
      case 'oi': return tick.oi || 0;
      case 'oi_change': return tick.oi_change || 0;
      case 'time': return tick.timestamp_ms || tick.timestamp || 0;
      default: return 0;
    }
  }, []);
  
  // Apply filters and sorting - OPTIMIZED for 200k+ ticks
  const filteredTicks = useMemo(() => {
    // Start with all ticks - no limit on initial processing
    let result = [...ticks];
    
    // FIRST: Filter out ticks outside market hours based on exchange
    result = result.filter(t => {
      // Must have valid LTP
      if (!t.last_price || t.last_price <= 0) return false;
      // Must have valid timestamp
      if (!t.timestamp && !t.timestamp_ms) return false;
      
      // Check if within market hours based on exchange
      const ts = t.timestamp_ms || (t.timestamp ? new Date(t.timestamp).getTime() : 0);
      if (!ts) return false;
      
      // Convert to IST (UTC + 5:30)
      const date = new Date(ts);
      const istHours = date.getUTCHours() + 5;
      const istMinutes = date.getUTCMinutes() + 30;
      const normalizedHours = istHours + Math.floor(istMinutes / 60);
      const normalizedMinutes = istMinutes % 60;
      const timeInHours = (normalizedHours >= 24 ? normalizedHours - 24 : normalizedHours) + (normalizedMinutes / 60);
      
      // NSE/BSE/NFO: 9:15 AM (9.25) to 3:30 PM (15.5) IST
      if (exchange === 'NSE' || exchange === 'BSE' || exchange === 'NFO') {
        if (timeInHours < 9.25 || timeInHours > 15.5) return false;
      }
      // MCX: 9:00 AM to 11:30 PM IST (commodities have extended hours)
      else if (exchange === 'MCX') {
        if (timeInHours < 9.0 || timeInHours > 23.5) return false;
      }
      // CDS (Currency): 9:00 AM to 5:00 PM IST
      else if (exchange === 'CDS') {
        if (timeInHours < 9.0 || timeInHours > 17.0) return false;
      }
      // Default: Allow all (don't filter)
      
      return true;
    });
    
    // Basic filter
    if (filter === 'buy') result = result.filter(t => t.agg_buy > 0);
    else if (filter === 'sell') result = result.filter(t => t.agg_sell > 0);
    
    // Advanced filters - use deferred values for smooth typing
    if (deferredMinLtq > 0) result = result.filter(t => (t.last_traded_quantity || 0) >= deferredMinLtq);
    
    // Bid/Ask Qty filters with AND/OR mode
    const hasBidQtyFilter = bidQtyEnabled && deferredMinBidQty > 0;
    const hasAskQtyFilter = askQtyEnabled && deferredMinAskQty > 0;
    
    if (hasBidQtyFilter || hasAskQtyFilter) {
      result = result.filter(t => {
        const bidQtyMatch = (t.bid_qty || 0) >= deferredMinBidQty;
        const askQtyMatch = (t.ask_qty || 0) >= deferredMinAskQty;
        
        // Both filters enabled
        if (hasBidQtyFilter && hasAskQtyFilter) {
          return bidAskQtyMode === 'AND' ? (bidQtyMatch && askQtyMatch) : (bidQtyMatch || askQtyMatch);
        }
        // Only Bid Qty enabled
        if (hasBidQtyFilter) {
          return bidQtyMatch;
        }
        // Only Ask Qty enabled
        if (hasAskQtyFilter) {
          return askQtyMatch;
        }
        return true;
      });
    }
    
    // Position % filters with AND/OR mode
    const posLessVal = parseFloat(posLessThanValue) || 0;
    const posGreaterVal = parseFloat(posGreaterThanValue) || 0;
    const hasPosLessThan = posLessThanEnabled && posLessThanValue !== '';
    const hasPosGreaterThan = posGreaterThanEnabled && posGreaterThanValue !== '';
    
    if (hasPosLessThan || hasPosGreaterThan) {
      result = result.filter(t => {
        const tradePos = calculateTradePosition(t);
        if (tradePos === null) return false;
        
        const lessThanMatch = tradePos < posLessVal;
        const greaterThanMatch = tradePos > posGreaterVal;
        
        // Both filters enabled
        if (hasPosLessThan && hasPosGreaterThan) {
          return posFilterMode === 'AND' ? (lessThanMatch && greaterThanMatch) : (lessThanMatch || greaterThanMatch);
        }
        // Only less than enabled
        if (hasPosLessThan) {
          return lessThanMatch;
        }
        // Only greater than enabled
        if (hasPosGreaterThan) {
          return greaterThanMatch;
        }
        return true;
      });
    }
    
    // LTP Diff filters with AND/OR mode (similar to AGG %)
    const ltpDiffLessVal = parseFloat(ltpDiffLessThanValue) || 0;
    const ltpDiffGreaterVal = parseFloat(ltpDiffGreaterThanValue) || 0;
    const hasLtpDiffLessThan = ltpDiffLessThanEnabled && ltpDiffLessThanValue !== '';
    const hasLtpDiffGreaterThan = ltpDiffGreaterThanEnabled && ltpDiffGreaterThanValue !== '';
    
    if (hasLtpDiffLessThan || hasLtpDiffGreaterThan) {
      result = result.filter(t => {
        // Use pre-calculated ltp_diff (already on tick object)
        const ltpDiff = t.ltp_diff || 0;
        
        const lessThanMatch = ltpDiff < ltpDiffLessVal;
        const greaterThanMatch = ltpDiff > ltpDiffGreaterVal;
        
        // Both filters enabled
        if (hasLtpDiffLessThan && hasLtpDiffGreaterThan) {
          return ltpDiffFilterMode === 'AND' ? (lessThanMatch && greaterThanMatch) : (lessThanMatch || greaterThanMatch);
        }
        // Only less than enabled
        if (hasLtpDiffLessThan) {
          return lessThanMatch;
        }
        // Only greater than enabled
        if (hasLtpDiffGreaterThan) {
          return greaterThanMatch;
        }
        return true;
      });
    }
    
    // Volume Change filter
    if (minVolChg > 0) result = result.filter(t => Math.abs(t.volume_change || 0) >= minVolChg);
    
    // Spread filters - OR logic when both enabled, single condition when only one enabled
    const hasSpreadLessThan = spreadLessThanEnabled && spreadLessThanValue > 0;
    const hasSpreadGreaterThan = spreadGreaterThanEnabled && spreadGreaterThanValue > 0;
    
    if (hasSpreadLessThan || hasSpreadGreaterThan) {
      result = result.filter(t => {
        const spread = (t.ask_price || 0) - (t.bid_price || 0);
        
        // Both filters enabled - use OR logic (show if spread < X OR spread > Y)
        if (hasSpreadLessThan && hasSpreadGreaterThan) {
          return spread < spreadLessThanValue || spread > spreadGreaterThanValue;
        }
        // Only less than enabled
        if (hasSpreadLessThan) {
          return spread < spreadLessThanValue;
        }
        // Only greater than enabled
        if (hasSpreadGreaterThan) {
          return spread > spreadGreaterThanValue;
        }
        return true;
      });
    }
    
    // Apply smart filters BEFORE sorting
    if (smartFilters.enabled) {
      // PERFORMANCE: Only calculate max on sample for large datasets
      const sampleForMax = result.length > 10000 ? result.slice(0, 10000) : result;
      const smartMaxValues = calculateMaxValues(sampleForMax);
      result = applySmartFilters(result, smartFilters, smartMaxValues);
      
      // If filter mode, only show matching rows
      if (smartFilters.mode === 'filter') {
        result = result.filter(t => t._smartFilterMatch);
      }
    }
    
    // Apply sorting AFTER smart filters
    // ALWAYS sort by time (newest first) unless user explicitly sorts by another column
    if (sortColumn && sortColumn !== 'time') {
      result = [...result].sort((a, b) => {
        const aVal = getSortValue(a, sortColumn);
        const bVal = getSortValue(b, sortColumn);
        const diff = aVal - bVal;
        return sortDirection === 'asc' ? diff : -diff;
      });
    } else {
      // Default: Sort by timestamp descending (newest first)
      result = [...result].sort((a, b) => {
        const tsA = a.timestamp_ms || 0;
        const tsB = b.timestamp_ms || 0;
        return tsB - tsA;
      });
    }
    
    // FILTER SAVE MODE PROCESSING
    if (filterSaveMode === FILTER_SAVE_MODES.SAVE) {
      const newTicks = [];
      result.forEach(tick => {
        const tickId = getTickUniqueId(tick);
        if (!savedTickIdsRef.current.has(tickId)) {
          savedTickIdsRef.current.add(tickId);
          newTicks.push({ ...tick, _savedAt: Date.now() });
        }
      });
      
      if (newTicks.length > 0) {
        setSavedTicks(prev => {
          const updated = [...newTicks, ...prev];
          if (updated.length > MAX_SAVED_TICKS) {
            const removed = updated.splice(MAX_SAVED_TICKS);
            removed.forEach(t => savedTickIdsRef.current.delete(getTickUniqueId(t)));
          }
          return updated;
        });
      }
    }
    
    // Determine what to display based on mode
    if (filterSaveMode === FILTER_SAVE_MODES.SHOW_ALL) {
      // Merge saved ticks with current filtered ticks (deduplicated)
      const mergedIds = new Set();
      const merged = [];
      
      result.forEach(tick => {
        const id = getTickUniqueId(tick);
        if (!mergedIds.has(id)) {
          mergedIds.add(id);
          merged.push(tick);
        }
      });
      
      savedTicks.forEach(tick => {
        const id = getTickUniqueId(tick);
        if (!mergedIds.has(id)) {
          mergedIds.add(id);
          merged.push(tick);
        }
      });
      
      // Sort by timestamp (newest first)
      merged.sort((a, b) => {
        const tsA = a.timestamp_ms || a.exchange_timestamp || 0;
        const tsB = b.timestamp_ms || b.exchange_timestamp || 0;
        return tsB - tsA;
      });
      
      return merged;
    }
    
    if (filterSaveMode === FILTER_SAVE_MODES.HIDE) {
      // Only show ticks that arrived after hide mode was enabled
      if (!hideTimestampRef.current) return [];
      return result.filter(tick => {
        const tickTs = tick.timestamp_ms || tick.exchange_timestamp || 0;
        return tickTs > hideTimestampRef.current;
      });
    }
    
    return result;
  }, [ticks, filter, deferredMinLtq, deferredMinBidQty, deferredMinAskQty, bidQtyEnabled, askQtyEnabled, bidAskQtyMode, posLessThanEnabled, posLessThanValue, posGreaterThanEnabled, posGreaterThanValue, posFilterMode, ltpDiffLessThanEnabled, ltpDiffLessThanValue, ltpDiffGreaterThanEnabled, ltpDiffGreaterThanValue, ltpDiffFilterMode, minVolChg, spreadLessThanEnabled, spreadLessThanValue, spreadGreaterThanEnabled, spreadGreaterThanValue, sortColumn, sortDirection, getSortValue, smartFilters, exchange, filterSaveMode, savedTicks]);
  
  // Handle column header click for sorting
  const handleColumnSort = useCallback((key) => {
    if (sortColumn === key) {
      // Toggle direction or clear
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else {
        // Clear sort - return to time order
        setSortColumn(null);
        setSortDirection('desc');
      }
    } else {
      // New column - start with descending
      setSortColumn(key);
      setSortDirection('desc');
    }
  }, [sortColumn, sortDirection]);
  
  // Column reorder functions
  const moveColumnUp = useCallback((key) => {
    setColumnOrder(prev => {
      const idx = prev.indexOf(key);
      if (idx > 0) {
        const newOrder = [...prev];
        [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
        return newOrder;
      }
      return prev;
    });
  }, []);
  
  const moveColumnDown = useCallback((key) => {
    setColumnOrder(prev => {
      const idx = prev.indexOf(key);
      if (idx < prev.length - 1) {
        const newOrder = [...prev];
        [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
        return newOrder;
      }
      return prev;
    });
  }, []);
  
  // Calculate row height including potential filter header
  const getRowHeight = (index) => {
    const tick = filteredTicks[index];
    const baseHeight = 28;
    // Add header height if this is first in a filter group
    if (smartFilters.enabled && tick?._smartFilterMatch && tick?._isFirstInGroup) {
      return baseHeight + 24;
    }
    return baseHeight;
  };
  
  const rowVirtualizer = useVirtualizer({
    count: filteredTicks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => getRowHeight(index),
    overscan: 30,
    // CRITICAL: This ensures the virtualizer always starts from index 0
    initialOffset: 0,
  });
  
  // Scroll to top when data is initially loaded or refreshed
  useEffect(() => {
    if (filteredTicks.length > 0) {
      // Scroll to top only if:
      // 1. This is initial load (lastTickCountRef was 0)
      // 2. New ticks were added at the beginning (count increased significantly)
      const wasEmpty = lastTickCountRef.current === 0;
      const significantNewTicks = filteredTicks.length - lastTickCountRef.current > 10;
      
      if (wasEmpty || (significantNewTicks && !hasScrolledToTopRef.current)) {
        // Use the virtualizer's official scrollToIndex API
        // This properly syncs the virtualizer's internal state with the scroll position
        setTimeout(() => {
          rowVirtualizer.scrollToIndex(0, { align: 'start', behavior: 'auto' });
          console.log(`[PopOut] Scrolled to index 0: wasEmpty=${wasEmpty}, tickCount=${filteredTicks.length}`);
          hasScrolledToTopRef.current = true;
        }, 50); // Small delay to ensure virtualizer has processed the new data
      }
      
      // Reset the scroll flag when tick count decreases (new date selected, etc.)
      if (filteredTicks.length < lastTickCountRef.current) {
        hasScrolledToTopRef.current = false;
      }
      
      lastTickCountRef.current = filteredTicks.length;
    }
  }, [filteredTicks.length, rowVirtualizer]);
  
  // Render cell with proper colors
  const renderCell = (tick, key, prevTick, sameSecond = false) => {
    switch(key) {
      case 'ltp': {
        const priceUp = prevTick && tick.last_price > prevTick.last_price;
        const priceDown = prevTick && tick.last_price < prevTick.last_price;
        const colorClass = priceUp ? 'text-red-400' : priceDown ? 'text-green-400' : 'text-white';
        return <span className={`font-mono text-[12px] font-bold ${colorClass}`}>{formatNumber(tick.last_price)}</span>;
      }
      case 'ltp_diff': {
        // Calculate ltp_diff lazily and cache on tick object (only for visible rows)
        if (tick.ltp_diff === undefined && prevTick) {
          tick.ltp_diff = (tick.last_price || 0) - (prevTick.last_price || 0);
        }
        const diff = tick.ltp_diff || 0;
        
        if (diff === 0) return <span className="text-[#71717a] text-[11px] font-mono">0.00</span>;
        
        // Determine intensity based on diff magnitude
        const absDiff = Math.abs(diff);
        const isSignificant = absDiff >= 1; // Significant move threshold
        const isHeavy = absDiff >= 2; // Heavy move threshold
        
        let bgClass = '';
        let textClass = '';
        let symbol = '';
        
        if (diff > 0) {
          // Price went UP (show green for buyer momentum)
          if (isHeavy) {
            bgClass = 'bg-green-600/40';
            textClass = 'text-green-300 font-bold';
            symbol = '🔥';
          } else if (isSignificant) {
            bgClass = 'bg-green-600/25';
            textClass = 'text-green-400 font-semibold';
          } else {
            textClass = 'text-green-400';
          }
        } else {
          // Price went DOWN (show red for seller momentum)
          if (isHeavy) {
            bgClass = 'bg-red-600/40';
            textClass = 'text-red-300 font-bold';
            symbol = '❄️';
          } else if (isSignificant) {
            bgClass = 'bg-red-600/25';
            textClass = 'text-red-400 font-semibold';
          } else {
            textClass = 'text-red-400';
          }
        }
        
        const displayValue = (diff > 0 ? '+' : '') + diff.toFixed(2);
        
        if (bgClass) {
          return (
            <div className={`relative h-5 ${bgClass} rounded-sm overflow-hidden flex items-center justify-center`}>
              <span className={`${textClass} text-[11px] font-mono`}>
                {symbol}{displayValue}
              </span>
            </div>
          );
        }
        
        return <span className={`${textClass} text-[11px] font-mono`}>{displayValue}</span>;
      }
      case 'bid_price': return <span className="text-green-400 font-mono text-[11px]">{formatNumber(tick.bid_price)}</span>;
      case 'ask_price': return <span className="text-red-400 font-mono text-[11px]">{formatNumber(tick.ask_price)}</span>;
      case 'bid_qty': return <DataBar value={tick.bid_qty} max={maxVals.maxBidQty} color="green" />;
      case 'ask_qty': return <DataBar value={tick.ask_qty} max={maxVals.maxAskQty} color="red" />;
      case 'trade_pos': {
        const tradePos = calculateTradePosition(tick);
        if (tradePos === null) return <span className="text-[#3f3f46] text-[11px]">-</span>;
        
        const isExtreme = tradePos > 100 || tradePos < 0;
        const isBuyerAggressive = tradePos >= 80;
        const isSellerAggressive = tradePos <= 20;
        const isHeavyBuyer = tradePos >= 90;
        const isHeavySeller = tradePos <= 10;
        
        let bgClass = 'bg-[#1a1a1a]';
        let textClass = 'text-white';
        
        if (isHeavyBuyer || tradePos > 100) {
          bgClass = 'bg-green-600/40';
          textClass = 'text-green-300 font-bold';
        } else if (isBuyerAggressive) {
          bgClass = 'bg-green-600/25';
          textClass = 'text-green-400';
        } else if (isHeavySeller || tradePos < 0) {
          bgClass = 'bg-red-600/40';
          textClass = 'text-red-300 font-bold';
        } else if (isSellerAggressive) {
          bgClass = 'bg-red-600/25';
          textClass = 'text-red-400';
        }
        
        return (
          <div className={`relative h-5 ${bgClass} rounded-sm overflow-hidden flex items-center justify-center`}>
            <span className={`${textClass} text-[11px] font-mono`}>
              {isExtreme && (tradePos > 100 ? '🔥' : '❄️')}{tradePos.toFixed(0)}%
            </span>
          </div>
        );
      }
      case 'spread': return <DataBar value={tick.spread} max={maxVals.maxSpread} color="yellow" />;
      case 'ltq': return <DataBar value={tick.last_traded_quantity} max={maxVals.maxLtq} color="blue" />;
      case 'vol_chg': return <DataBar value={Math.abs(tick.volume_change || 0)} max={maxVals.maxVolChg} color="cyan" />;
      case 'volume': return <DataBar value={tick.volume || tick.volume_traded} max={maxVals.maxVolume} color="purple" />;
      case 'oi': return <DataBar value={tick.oi} max={maxVals.maxOi} color="orange" />;
      case 'oi_change': {
        const oiChg = tick.oi_change || 0;
        return <DataBar 
          value={Math.abs(oiChg)} 
          max={maxVals.maxOiChg} 
          color={oiChg >= 0 ? "green" : "red"} 
          showSign={true}
          originalValue={oiChg}
        />;
      }
      case 'time': {
        const timeColor = sameSecond ? 'text-cyan-400 font-bold' : 'text-[#a1a1aa]';
        return <span className={`${timeColor} text-[11px] font-mono`}>{formatTimeIST(tick.timestamp)}</span>;
      }
      default: return '-';
    }
  };
  
  // Get exchange badge color
  const getExchangeBadge = () => {
    if (exchange === 'MCX') return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    if (exchange === 'NFO') return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };
  
  // Toggle column visibility - use startTransition for smooth UI
  const [isColumnPending, startColumnTransition] = useTransition();
  const toggleColumn = (key) => {
    startColumnTransition(() => {
      setVisibleCols(prev => 
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
      );
    });
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilter('all');
    setMinLtq(0);
    setMinBidQty(0);
    setMinAskQty(0);
    setBidQtyEnabled(false);
    setAskQtyEnabled(false);
    setBidAskQtyMode('AND');
    setPosLessThanEnabled(false);
    setPosLessThanValue('');
    setPosGreaterThanEnabled(false);
    setPosGreaterThanValue('');
    setPosFilterMode('OR');
    setMinVolChg(0);
    setSpreadLessThanEnabled(false);
    setSpreadLessThanValue(0);
    setSpreadGreaterThanEnabled(false);
    setSpreadGreaterThanValue(0);
  };
  
  return (
    <div className="h-screen w-screen bg-[#0a0a0a] text-white flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 bg-gradient-to-r from-[#1a1a1a] to-[#0f0f0f] border-b border-[#333] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-500/50" />
          <span className="text-lg font-bold text-white">{symbol}</span>
          <span className={`text-[10px] px-2 py-0.5 rounded border ${getExchangeBadge()}`}>{exchange}</span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Date Selector */}
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4 text-[#71717A]" />
            <Select value={selectedDate} onValueChange={setSelectedDate}>
              <SelectTrigger className="h-8 w-32 bg-[#111] border-[#333] text-xs">
                <SelectValue placeholder="Select date" />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#333]">
                {availableDates.map(d => (
                  <SelectItem key={d.date} value={d.date}>
                    {d.date} ({formatVolume(d.tick_count)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Filter */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-20 bg-[#111] border-[#333] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#111] border-[#333]">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="buy">Buys</SelectItem>
              <SelectItem value="sell">Sells</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Advanced Filter Toggle */}
          <Button
            variant={showAdvanced ? "default" : "ghost"}
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`h-8 px-2 ${showAdvanced ? 'bg-blue-600 text-white' : 'text-[#71717A] hover:text-white'}`}
          >
            <Filter className="w-4 h-4" />
          </Button>
          
          {/* Column Selector */}
          <Button
            variant={showColumns ? "default" : "ghost"}
            size="sm"
            onClick={() => setShowColumns(!showColumns)}
            className={`h-8 px-2 ${showColumns ? 'bg-purple-600 text-white' : 'text-[#71717A] hover:text-white'}`}
          >
            Cols ({visibleCols.length})
          </Button>
          
          {/* Load More */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadMoreTicks()}
            disabled={isLoading || !hasMore}
            className="h-8 px-3 bg-[#111] border-[#333] text-cyan-400 hover:text-cyan-300"
          >
            {isLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1" /> +5K</>}
          </Button>
          
          {/* Load Full Day */}
          <Button
            variant="outline"
            size="sm"
            onClick={loadFullDay}
            disabled={isLoadingFullDay}
            className="h-8 px-3 bg-[#111] border-yellow-600/50 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20"
          >
            {isLoadingFullDay ? (
              <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Loading...</>
            ) : (
              <><Download className="w-4 h-4 mr-1" /> Full Day</>
            )}
          </Button>
          
          {/* Manual Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={loadPendingTicks}
            disabled={isRefreshing}
            className="h-8 px-3 bg-[#111] border-blue-600/50 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
            data-testid="refresh-ticks-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          
          {/* Auto-Refresh Toggle and Interval */}
          <div className="flex items-center gap-2 px-2 py-1 bg-[#0a0a0a] border border-[#333] rounded">
            <span className="text-[10px] text-[#71717A]">Auto:</span>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`w-8 h-4 rounded-full transition-colors ${autoRefresh ? 'bg-green-500' : 'bg-[#333]'}`}
              data-testid="auto-refresh-toggle"
            >
              <div className={`w-3 h-3 rounded-full bg-white transition-transform mx-0.5 ${autoRefresh ? 'translate-x-3.5' : ''}`} />
            </button>
            {autoRefresh && (
              <>
                <span className="text-[10px] text-[#71717A]">Every</span>
                <Select value={String(autoRefreshInterval)} onValueChange={(v) => setAutoRefreshInterval(Number(v))}>
                  <SelectTrigger className="h-6 w-16 text-[10px] bg-[#0a0a0a] border-[#333]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.083">5 sec</SelectItem>
                    <SelectItem value="0.167">10 sec</SelectItem>
                    <SelectItem value="0.5">30 sec</SelectItem>
                    <SelectItem value="1">1 min</SelectItem>
                    <SelectItem value="2">2 min</SelectItem>
                    <SelectItem value="5">5 min</SelectItem>
                    <SelectItem value="10">10 min</SelectItem>
                    <SelectItem value="15">15 min</SelectItem>
                    <SelectItem value="30">30 min</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
          
          {/* Last Refresh Time */}
          {lastRefreshTime && (
            <span className="text-[9px] text-[#52525B]">
              Last: {lastRefreshTime.toLocaleTimeString()}
              {pendingTicksCount > 0 && <span className="text-green-400 ml-1">(+{pendingTicksCount})</span>}
            </span>
          )}
        </div>
      </div>
      
      {/* Advanced Filters Panel */}
      {showAdvanced && (
        <div className="px-4 py-2 bg-[#111] border-b border-[#333] flex items-center gap-4 flex-wrap">
          <span className="text-[11px] text-[#71717A] font-semibold">Advanced Filters:</span>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-[#52525B]">LTQ ≥</span>
            <Input type="number" value={minLtq || ''} onChange={(e) => setMinLtq(parseInt(e.target.value) || 0)} 
              className="h-6 w-16 text-[10px] bg-[#0a0a0a] border-[#27272A] px-2" placeholder="0" />
          </div>
          
          {/* Bid/Ask Qty Filters with AND/OR Toggle */}
          <div className="flex items-center gap-2 border-l border-[#333] pl-3">
            <span className="text-[10px] text-green-400 font-medium">Bid/Ask Qty:</span>
            
            {/* AND/OR Toggle */}
            <button
              onClick={() => setBidAskQtyMode(prev => prev === 'AND' ? 'OR' : 'AND')}
              className={`h-5 px-2 text-[9px] rounded font-bold transition-all ${
                bidAskQtyMode === 'AND'
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                  : 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
              }`}
              title={bidAskQtyMode === 'AND' ? 'Both conditions must match' : 'Either condition can match'}
            >
              {bidAskQtyMode}
            </button>
            
            {/* Bid Qty Filter */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setBidQtyEnabled(!bidQtyEnabled)}
                className={`h-5 px-2 text-[9px] rounded transition-all ${
                  bidQtyEnabled 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                    : 'bg-[#1a1a1a] text-[#71717A] border border-[#333]'
                }`}
              >
                Bid ≥
              </button>
              <Input 
                type="number" 
                value={minBidQty || ''} 
                onChange={(e) => setMinBidQty(parseInt(e.target.value) || 0)}
                placeholder="0"
                disabled={!bidQtyEnabled}
                className={`h-6 w-14 text-[10px] bg-[#0a0a0a] border-[#27272A] px-2 text-white ${!bidQtyEnabled ? 'opacity-40' : ''}`}
              />
            </div>
            
            {/* Ask Qty Filter */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setAskQtyEnabled(!askQtyEnabled)}
                className={`h-5 px-2 text-[9px] rounded transition-all ${
                  askQtyEnabled 
                    ? 'bg-red-500/20 text-red-400 border border-red-500/50' 
                    : 'bg-[#1a1a1a] text-[#71717A] border border-[#333]'
                }`}
              >
                Ask ≥
              </button>
              <Input 
                type="number" 
                value={minAskQty || ''} 
                onChange={(e) => setMinAskQty(parseInt(e.target.value) || 0)}
                placeholder="0"
                disabled={!askQtyEnabled}
                className={`h-6 w-14 text-[10px] bg-[#0a0a0a] border-[#27272A] px-2 text-white ${!askQtyEnabled ? 'opacity-40' : ''}`}
              />
            </div>
          </div>
          
          {/* Position % Filters with AND/OR Toggle */}
          <div className="flex items-center gap-2 border-l border-[#333] pl-3">
            <span className="text-[10px] text-cyan-400 font-medium">Agg %:</span>
            
            {/* AND/OR Toggle */}
            <button
              onClick={() => setPosFilterMode(prev => prev === 'AND' ? 'OR' : 'AND')}
              className={`h-5 px-2 text-[9px] rounded font-bold transition-all ${
                posFilterMode === 'AND'
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                  : 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
              }`}
              title={posFilterMode === 'AND' ? 'Both conditions must match' : 'Either condition can match'}
            >
              {posFilterMode}
            </button>
            
            {/* Less Than */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPosLessThanEnabled(!posLessThanEnabled)}
                className={`h-5 px-2 text-[9px] rounded transition-all ${
                  posLessThanEnabled 
                    ? 'bg-red-500/20 text-red-400 border border-red-500/50' 
                    : 'bg-[#1a1a1a] text-[#71717A] border border-[#333]'
                }`}
              >
                &lt;
              </button>
              <Input 
                type="number" 
                value={posLessThanValue} 
                onChange={(e) => setPosLessThanValue(e.target.value)}
                placeholder="0"
                disabled={!posLessThanEnabled}
                className={`h-6 w-12 text-[10px] bg-[#0a0a0a] border-[#27272A] px-2 text-white ${!posLessThanEnabled ? 'opacity-40' : ''}`}
              />
            </div>
            
            {/* Greater Than */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPosGreaterThanEnabled(!posGreaterThanEnabled)}
                className={`h-5 px-2 text-[9px] rounded transition-all ${
                  posGreaterThanEnabled 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                    : 'bg-[#1a1a1a] text-[#71717A] border border-[#333]'
                }`}
              >
                &gt;
              </button>
              <Input 
                type="number" 
                value={posGreaterThanValue} 
                onChange={(e) => setPosGreaterThanValue(e.target.value)}
                placeholder="0"
                disabled={!posGreaterThanEnabled}
                className={`h-6 w-12 text-[10px] bg-[#0a0a0a] border-[#27272A] px-2 text-white ${!posGreaterThanEnabled ? 'opacity-40' : ''}`}
              />
            </div>
          </div>
          
          {/* LTP Diff Filters with AND/OR Toggle */}
          <div className="flex items-center gap-2 border-l border-[#333] pl-3">
            <span className="text-[10px] text-blue-400 font-medium">LTP Diff:</span>
            
            {/* AND/OR Toggle */}
            <button
              onClick={() => setLtpDiffFilterMode(prev => prev === 'AND' ? 'OR' : 'AND')}
              className={`h-5 px-2 text-[9px] rounded font-bold transition-all ${
                ltpDiffFilterMode === 'AND'
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                  : 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
              }`}
              title={ltpDiffFilterMode === 'AND' ? 'Both conditions must match' : 'Either condition can match'}
            >
              {ltpDiffFilterMode}
            </button>
            
            {/* Less Than */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setLtpDiffLessThanEnabled(!ltpDiffLessThanEnabled)}
                className={`h-5 px-2 text-[9px] rounded transition-all ${
                  ltpDiffLessThanEnabled 
                    ? 'bg-red-500/20 text-red-400 border border-red-500/50' 
                    : 'bg-[#1a1a1a] text-[#71717A] border border-[#333]'
                }`}
              >
                &lt;
              </button>
              <Input 
                type="number" 
                value={ltpDiffLessThanValue} 
                onChange={(e) => setLtpDiffLessThanValue(e.target.value)}
                placeholder="0"
                disabled={!ltpDiffLessThanEnabled}
                className={`h-6 w-14 text-[10px] bg-[#0a0a0a] border-[#27272A] px-2 text-white ${!ltpDiffLessThanEnabled ? 'opacity-40' : ''}`}
              />
            </div>
            
            {/* Greater Than */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setLtpDiffGreaterThanEnabled(!ltpDiffGreaterThanEnabled)}
                className={`h-5 px-2 text-[9px] rounded transition-all ${
                  ltpDiffGreaterThanEnabled 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                    : 'bg-[#1a1a1a] text-[#71717A] border border-[#333]'
                }`}
              >
                &gt;
              </button>
              <Input 
                type="number" 
                value={ltpDiffGreaterThanValue} 
                onChange={(e) => setLtpDiffGreaterThanValue(e.target.value)}
                placeholder="0"
                disabled={!ltpDiffGreaterThanEnabled}
                className={`h-6 w-14 text-[10px] bg-[#0a0a0a] border-[#27272A] px-2 text-white ${!ltpDiffGreaterThanEnabled ? 'opacity-40' : ''}`}
              />
            </div>
          </div>
          
          {/* Volume Change Filter */}
          <div className="flex items-center gap-1 border-l border-[#333] pl-3">
            <span className="text-[10px] text-purple-400">Vol Chg ≥</span>
            <Input type="number" value={minVolChg || ''} onChange={(e) => setMinVolChg(parseInt(e.target.value) || 0)} 
              className="h-6 w-16 text-[10px] bg-[#0a0a0a] border-purple-500/30 px-2 text-white" placeholder="0" />
          </div>
          
          {/* Spread Filters with Toggle */}
          <div className="flex items-center gap-2 border-l border-[#333] pl-3">
            <span className="text-[10px] text-amber-400 font-medium">Spread:</span>
            
            {/* Less Than */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSpreadLessThanEnabled(!spreadLessThanEnabled)}
                className={`h-5 px-2 text-[9px] rounded transition-all ${
                  spreadLessThanEnabled 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                    : 'bg-[#1a1a1a] text-[#71717A] border border-[#333]'
                }`}
              >
                &lt;
              </button>
              <Input 
                type="number" 
                value={spreadLessThanValue || ''} 
                onChange={(e) => setSpreadLessThanValue(parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={!spreadLessThanEnabled}
                className={`h-6 w-14 text-[10px] bg-[#0a0a0a] border-[#27272A] px-2 text-white ${!spreadLessThanEnabled ? 'opacity-40' : ''}`}
              />
            </div>
            
            {/* Greater Than */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setSpreadGreaterThanEnabled(!spreadGreaterThanEnabled)}
                className={`h-5 px-2 text-[9px] rounded transition-all ${
                  spreadGreaterThanEnabled 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                    : 'bg-[#1a1a1a] text-[#71717A] border border-[#333]'
                }`}
              >
                &gt;
              </button>
              <Input 
                type="number" 
                value={spreadGreaterThanValue || ''} 
                onChange={(e) => setSpreadGreaterThanValue(parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={!spreadGreaterThanEnabled}
                className={`h-6 w-14 text-[10px] bg-[#0a0a0a] border-[#27272A] px-2 text-white ${!spreadGreaterThanEnabled ? 'opacity-40' : ''}`}
              />
            </div>
          </div>
          
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-6 px-2 text-[10px] text-red-400 hover:text-red-300">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
          
          {/* Smart Filters */}
          <div className="border-l border-[#333] pl-4">
            <SmartFiltersPanel filters={smartFilters} setFilters={setSmartFilters} isCompact={true} />
          </div>
        </div>
      )}
      
      {/* Column Selector Panel with Reorder */}
      {showColumns && (
        <div className="px-4 py-2 bg-[#111] border-b border-[#333]">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] text-[#71717A] font-semibold">Columns (click to toggle, arrows to reorder):</span>
            {sortColumn && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setSortColumn(null); setSortDirection('desc'); }}
                className="h-5 px-2 text-[9px] text-orange-400 hover:text-orange-300"
              >
                Clear Sort
              </Button>
            )}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {columnOrder.map((key, idx) => {
              const col = COLUMNS.find(c => c.key === key);
              if (!col) return null;
              return (
                <div key={key} className="flex items-center bg-[#0a0a0a] border border-[#333] rounded">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveColumnUp(key)}
                    disabled={idx === 0}
                    className="h-6 px-1 text-[#52525B] hover:text-white disabled:opacity-30"
                  >
                    <ChevronUp className="w-3 h-3" />
                  </Button>
                  <Button
                    variant={visibleCols.includes(key) ? "default" : "ghost"}
                    size="sm"
                    onClick={() => toggleColumn(key)}
                    className={`h-6 px-2 text-[9px] rounded-none ${visibleCols.includes(key) ? 'bg-purple-600 text-white' : 'text-[#71717A]'}`}
                  >
                    {col.label}
                    {sortColumn === key && (
                      <span className="ml-1 text-yellow-400">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveColumnDown(key)}
                    disabled={idx === columnOrder.length - 1}
                    className="h-6 px-1 text-[#52525B] hover:text-white disabled:opacity-30"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Table - Single Symbol View */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Row - Clickable for sorting */}
        <div className="flex bg-gradient-to-r from-[#1a1a1a] to-[#151515] border-b border-[#333] text-[10px] font-bold select-none">
          {columnOrder.filter(k => visibleCols.includes(k)).map(key => (
            <div 
              key={key}
              style={{ flex: 1, minWidth: COLUMN_MIN_WIDTHS[key] || 60 }}
              onClick={() => handleColumnSort(key)}
              className={`px-2 py-2 text-center whitespace-nowrap uppercase tracking-wider cursor-pointer hover:bg-[#252525] transition-colors ${
                key === 'agg_buy' ? 'text-green-400 bg-green-900/30' : 
                key === 'agg_sell' ? 'text-red-400 bg-red-900/30' : 
                sortColumn === key ? 'text-yellow-400 bg-yellow-900/20' :
                'text-[#d4d4d8]'
              }`}
            >
              <span className="flex items-center justify-center gap-1">
                {COLUMNS.find(c => c.key === key)?.label}
                {sortColumn === key && (
                  <span className="text-yellow-400 text-[8px]">
                    {sortDirection === 'asc' ? '▲' : '▼'}
                  </span>
                )}
                {sortColumn !== key && (
                  <span className="text-[#52525B] text-[8px] opacity-0 group-hover:opacity-100">⇅</span>
                )}
              </span>
            </div>
          ))}
        </div>
        
        {/* Virtualized Rows */}
        <div className="flex-1 overflow-auto" ref={parentRef}>
          <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
            {rowVirtualizer.getVirtualItems().map(vRow => {
              const tick = filteredTicks[vRow.index];
              const prevTick = vRow.index > 0 ? filteredTicks[vRow.index - 1] : null;
              const sameSecond = prevTick && getSeconds(tick.timestamp) === getSeconds(prevTick.timestamp);
              
              // Smart filter highlight class
              const smartFilterClass = smartFilters.enabled ? getSmartFilterRowClass(tick, smartFilters) : '';
              
              // Check if this is first row in a filter group - show header (only if labels enabled)
              const showFilterHeader = smartFilters.enabled && tick._smartFilterMatch && tick._isFirstInGroup && smartFilters.showLabels;
              const filterHeader = showFilterHeader ? getFilterHeaderRow(tick, true, false, smartFilters.showLabels) : null;
              
              // Calculate heights
              const headerHeight = showFilterHeader ? 24 : 0;
              const rowHeight = 28;
              
              // Use columnOrder for consistent ordering
              const orderedVisibleCols = columnOrder.filter(k => visibleCols.includes(k));
              
              return (
                <div
                  key={vRow.index}
                  className="absolute left-0 right-0"
                  style={{ 
                    top: `${vRow.start}px`,
                    height: rowHeight + headerHeight,
                  }}
                >
                  {/* Filter Header Row - shows above matched group */}
                  {filterHeader}
                  
                  {/* Data Row */}
                  <div
                    className={`flex items-center text-[11px] border-b border-[#1a1a1a] hover:bg-[#1a1a1a] transition-colors ${smartFilterClass}`}
                    style={{ height: `${rowHeight}px` }}
                  >
                    {orderedVisibleCols.map(key => {
                      let cellBg = '';
                      if (key === 'agg_buy' && tick.agg_buy > 0) cellBg = 'bg-green-900/40';
                      else if (key === 'agg_sell' && tick.agg_sell > 0) cellBg = 'bg-red-900/40';
                      
                      return (
                        <div 
                          key={key}
                          style={{ flex: 1, minWidth: COLUMN_MIN_WIDTHS[key] || 60 }}
                          className={`px-1 text-center ${cellBg}`}
                        >
                          {renderCell(tick, key, prevTick, sameSecond)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Manual Bar % Reference Row - User inputs aligned with columns */}
      <div className="flex bg-gradient-to-r from-amber-900/30 via-amber-800/20 to-amber-900/30 border-t-2 border-amber-500 text-[10px]">
        {columnOrder.filter(k => visibleCols.includes(k)).map(key => {
          // Only show input for columns that support bar % (numeric data bar columns)
          const isBarColumn = ['bid_qty', 'ask_qty', 'ltq', 'spread', 'vol_chg', 'volume', 'oi', 'oi_change', 'agg_buy', 'agg_sell'].includes(key);
          
          return (
            <div 
              key={key}
              style={{ flex: 1, minWidth: COLUMN_MIN_WIDTHS[key] || 60 }}
              className="px-1 py-1.5 text-center"
            >
              {isBarColumn ? (
                <input
                  type="number"
                  value={refValues[key] || ''}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setRefValues(prev => ({ ...prev, [key]: val }));
                  }}
                  placeholder={key === 'bid_qty' ? 'REF' : '-'}
                  className="w-full h-6 px-1 text-[10px] font-mono text-center bg-[#0a0a0a] border border-amber-600/50 rounded text-amber-300 placeholder-amber-600/40 focus:outline-none focus:border-amber-400"
                  data-testid={`ref-input-${key}`}
                />
              ) : (
                <span className="text-amber-500/50">-</span>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Footer - Single View */}
      <div className="h-10 px-4 bg-gradient-to-r from-[#1a1a1a] via-[#151515] to-[#1a1a1a] border-t border-[#333] flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-bold text-white">{filteredTicks.length.toLocaleString()}</span>
            <span className="text-[11px] text-[#a1a1aa]">/ {ticks.length.toLocaleString()} loaded</span>
            <span className="text-[10px] text-[#52525B]">({totalTicks.toLocaleString()} total)</span>
          </div>
          {savedTicks.length > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded bg-purple-900/40 text-purple-400 font-bold">
              💾 {savedTicks.length.toLocaleString()} saved
            </span>
          )}
          {hasMore && (
            <span className="text-[10px] text-cyan-400">More history available</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Filter Save Mode Toggle */}
          <FilterSaveModeToggle
            mode={filterSaveMode}
            setMode={setFilterSaveMode}
            savedCount={savedTicks.length}
            onDownload={downloadSavedTicks}
            onClear={clearSavedTicks}
            autoDownload={autoDownload}
            setAutoDownload={setAutoDownload}
            compact={false}
          />
          <div className="w-px h-6 bg-[#333]" />
          <span className="text-[10px] px-2 py-1 rounded bg-green-900/40 text-green-400 font-bold">
            Buys: {filteredTicks.filter(t => t.agg_buy > 0).length.toLocaleString()}
          </span>
          <span className="text-[10px] px-2 py-1 rounded bg-red-900/40 text-red-400 font-bold">
            Sells: {filteredTicks.filter(t => t.agg_sell > 0).length.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
